import React, { useEffect, useRef, useState } from "react";
import { InstagramLogo, ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Admin-curated Instagram carousel for the homepage.
 * Pulls /api/instagram-posts (published only); hides itself if empty.
 */
export default function InstagramCarousel() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const trackRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    api
      .get("/instagram-posts")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  // Track scroll position → active bullet + page count.
  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length === 0) return undefined;
    const update = () => {
      const pages = Math.max(1, Math.ceil(el.scrollWidth / el.clientWidth));
      setPageCount(pages);
      setPage(Math.min(pages - 1, Math.round(el.scrollLeft / el.clientWidth)));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [items]);

  // Auto-scroll every 3.5s, looping back to the start; paused on hover/touch.
  useEffect(() => {
    if (items.length === 0) return undefined;
    const id = setInterval(() => {
      const el = trackRef.current;
      if (!el || pausedRef.current) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 8;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + el.clientWidth, behavior: "smooth" });
    }, 3500);
    return () => clearInterval(id);
  }, [items]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  const goToPage = (i) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
  };

  if (items.length === 0) return null;

  return (
    <section
      className="md-section md-section-tinted md-instagram-section"
      data-testid="instagram-carousel-section"
    >
      <div className="md-container">
        <div className="md-instagram-head">
          <div>
            <div
              className="md-label-eyebrow mb-2"
              style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", whiteSpace: "nowrap" }}
            >
              <InstagramLogo size={14} weight="bold" />
              <span>@minddhara</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-serif" data-testid="instagram-title">
              Join Our Community
            </h2>
            <p className="text-base mt-3" style={{ color: "var(--md-text-soft)", maxWidth: 520 }}>
              Daily nudges on cognitive wellbeing, therapist reflections and a peek behind the
              scenes, follow along on Instagram.
            </p>
          </div>
          <div className="md-instagram-actions">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="md-articles-arrow"
              aria-label="Previous posts"
              data-testid="ig-carousel-prev"
            >
              <ArrowLeft size={16} weight="bold" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="md-articles-arrow"
              aria-label="Next posts"
              data-testid="ig-carousel-next"
            >
              <ArrowRight size={16} weight="bold" />
            </button>
            <a
              href="https://www.instagram.com/minddhara"
              target="_blank"
              rel="noreferrer noopener"
              className="btn-primary md-instagram-followcta"
              data-testid="ig-follow-cta"
            >
              Follow Us <InstagramLogo size={14} weight="bold" />
            </a>
          </div>
        </div>

        <div
          className="md-instagram-track"
          ref={trackRef}
          data-testid="ig-carousel-track"
          onMouseEnter={() => { pausedRef.current = true; }}
          onMouseLeave={() => { pausedRef.current = false; }}
          onTouchStart={() => { pausedRef.current = true; }}
          onTouchEnd={() => { pausedRef.current = false; }}
        >
          {items.map((p) => (
            <a
              key={p.id}
              href={p.permalink || "https://www.instagram.com/minddhara"}
              target="_blank"
              rel="noreferrer noopener"
              className="md-instagram-card"
              data-testid={`ig-card-${p.id}`}
            >
              <div className="md-instagram-card-img">
                <img src={p.image_url} alt={p.caption || "MindDhara on Instagram"} loading="lazy" />
                <span className="md-instagram-card-icon" aria-hidden>
                  <InstagramLogo size={18} weight="bold" />
                </span>
              </div>
              {p.caption && <p className="md-instagram-card-caption">{p.caption}</p>}
            </a>
          ))}
        </div>

        {pageCount > 1 && (
          <div className="md-instagram-bullets" data-testid="ig-carousel-bullets" role="tablist" aria-label="Instagram carousel pages">
            {Array.from({ length: pageCount }).map((_, i) => (
              <button
                key={i}
                type="button"
                className={`md-instagram-bullet ${i === page ? "active" : ""}`}
                onClick={() => goToPage(i)}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`ig-bullet-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
